import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HomeTitle } from './HomeSectionHead';
import { formatUSD } from '../../utils/money';

// Ink-panel Academy cross-sell for the marketing homepage (Home.jsx).
export default function HomeAcademyCrossSell() {
  const [courses, setCourses] = useState([]);
  const [tutorials, setTutorials] = useState([]);
  const [seminars, setSeminars] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);

  useEffect(() => {
    fetch('/api/academy/courses')
      .then((r) => r.json())
      .then((d) => setCourses(Array.isArray(d) ? d : []))
      .catch(() => {})
      .finally(() => setCoursesLoading(false));
    fetch('/api/academy/tutorials')
      .then((r) => r.json())
      .then((d) => setTutorials(Array.isArray(d) ? d : []))
      .catch(() => {});
    fetch('/api/academy/seminars')
      .then((r) => r.json())
      .then((d) => setSeminars(Array.isArray(d) ? d : []))
      .catch(() => {});
    fetch('/api/academy/podcasts')
      .then((r) => r.json())
      .then((d) => setPodcasts(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  return (
    <section className="bg-press-ink" aria-label="UniFreelancer Academy">
      <div className="mx-auto max-w-page px-6 py-16 sm:px-10 lg:px-16">
        <div className="flex items-end justify-between gap-4">
          <div className="min-w-0 flex-1">
            <HomeTitle
              dark
              title="UniFreelancer Academy"
              desc="Courses, tutorials and seminars from people who’ve actually done the work."
            />
          </div>
          <Link
            to="/academy"
            className="whitespace-nowrap text-[13px] font-semibold text-press-brass-light transition hover:text-press-paper"
          >
            Explore all →
          </Link>
        </div>

        {coursesLoading ? (
          <div className="mt-8 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
            <div className="h-[320px] rounded-[14px] bg-white/[0.04]" />
            <div className="flex flex-col gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-[88px] rounded-[12px] bg-white/[0.04]" />
              ))}
            </div>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 items-stretch gap-3 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
            {courses[0] ? (
              <Link
                to={`/academy/courses/${courses[0]._id}`}
                className="flex flex-col overflow-hidden rounded-[14px] border border-white/10 bg-white/[0.03] transition hover:border-press-ink/40"
              >
                <div
                  className="relative h-[180px] flex-none"
                  style={{
                    background: courses[0].thumbnail
                      ? `url(${courses[0].thumbnail}) center/cover no-repeat`
                      : 'linear-gradient(135deg,#1c222c,#12161C)',
                  }}
                >
                  <span className="absolute left-3 top-3 rounded-full bg-press-paper px-2.5 py-1 text-[12px] font-bold text-press-ink">
                    Course
                  </span>
                  <span className="absolute right-3 top-3 rounded-full bg-black/50 px-2.5 py-1 text-[12px] font-semibold text-press-paper backdrop-blur">
                    {courses[0].pricing?.amount ? formatUSD(courses[0].pricing.amount) : 'Free'}
                  </span>
                </div>
                <div className="flex flex-1 flex-col gap-1.5 p-6">
                  {courses[0].difficulty && (
                    <span className="text-[12px] font-semibold text-press-brass-light">
                      {courses[0].difficulty}
                      {courses[0].category && ` · ${courses[0].category}`}
                    </span>
                  )}
                  <h3 className="font-press-display text-[1.25rem] font-normal leading-[1.25] text-press-paper">
                    {courses[0].title}
                  </h3>
                  <p className="text-[12px] text-press-paper/60">
                    {courses[0].instructor?.name && `by ${courses[0].instructor.name}`}
                    {courses[0].duration && ` · ${courses[0].duration}`}
                  </p>
                  {courses[0].learningPoints?.[0] && (
                    <p className="mt-1 line-clamp-2 text-[13px] leading-[1.55] text-press-paper/55">
                      {courses[0].learningPoints[0]}
                    </p>
                  )}
                </div>
              </Link>
            ) : (
              <div className="flex items-center justify-center rounded-[14px] border border-dashed border-white/10 bg-white/[0.02] text-[13px] text-press-paper/30">
                No courses yet
              </div>
            )}

            <div className="flex flex-col gap-3">
              {[
                tutorials[0] && {
                  kind: 'Tutorial',
                  to: `/academy/tutorials/${tutorials[0]._id}`,
                  title: tutorials[0].title,
                  meta: [tutorials[0].instructor?.name && `by ${tutorials[0].instructor.name}`, tutorials[0].duration].filter(Boolean).join(' · '),
                },
                seminars[0] && {
                  kind: 'Webinar',
                  to: `/academy/seminars/${seminars[0]._id}`,
                  title: seminars[0].title,
                  meta: [seminars[0].speaker?.name && `with ${seminars[0].speaker.name}`, seminars[0].duration].filter(Boolean).join(' · '),
                },
                podcasts[0] && {
                  kind: 'Podcast',
                  to: '/academy/podcasts',
                  title: podcasts[0].title,
                  meta: [podcasts[0].speaker && `by ${podcasts[0].speaker}`, podcasts[0].duration].filter(Boolean).join(' · '),
                },
              ]
                .filter(Boolean)
                .map((item) => (
                  <Link
                    key={item.kind}
                    to={item.to}
                    className="flex flex-1 flex-col justify-center gap-1 rounded-[12px] border border-white/10 bg-white/[0.03] p-4 transition hover:border-press-ink/40"
                  >
                    <span className="text-[12px] font-bold text-press-brass-light">
                      {item.kind}
                    </span>
                    <p className="font-press-display text-[1rem] font-normal leading-[1.3] text-press-paper">
                      {item.title}
                    </p>
                    {item.meta && <p className="text-[12px] text-press-paper/60">{item.meta}</p>}
                  </Link>
                ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
